/**
 * Armenian Belot orchestrator: deal → auction → declare → play → score → next round / match end.
 *
 * **Tricks:** 32 cards ⇒ **8** tricks per round; last trick gets +10 in {@link ./scoring}.
 *
 * **Торги:** первый говорит `(dealerSeat + 1) % 4`, далее по часовой стрелке; открытие **10 CP** (`tens ≥ 10`).
 * **Контра / реконтра** — в фазе торгов (`auction`), пока `auction.phase === 'bidding'` и есть лучшая заявка;
 * контра — только защитники (команда ≠ команда автора заявки); реконтра — только команда автора заявки.
 *
 * Commands: `START_ROUND`, `BID`, `PASS`, `CONTRA`, `RECONTRA`, `END_DECLARE_PHASE`,
 * `COMMIT_FIRST_PLAY_DECLARATIONS`, `PLAY_CARD` (optional `payload.declareBella`, optional
 * `payload.commitDeclaredKeys` in hardcore before first card of the seat).
 *
 * **Комбинации:** `declarationMode: 'casual'` — при `END_DECLARE_PHASE` полный авто-поиск и
 * сравнение команд. `'hardcore'` — очки за комбы только по явному выбору на первом ходу игрока
 * (`COMMIT_FIRST_PLAY_DECLARATIONS` или `PLAY_CARD.payload.commitDeclaredKeys`).
 *
 * @module game/belot/armenian/engine
 */

const {
  TRUMP_NO_TRUMP,
  TRUMP_ALL_TRUMP,
  KAPOT_CONTRACT_POINTS,
  createInitialAuctionState,
  applyAuctionCommand,
} = require("./auction");
const { buildDeck, shuffle, deal323 } = require("./deck");
const { teamOfSeat } = require("./types");
const { createRngMeta, rngFromMeta } = require("../../../../game/belotArmenian/mulberry32");
const {
  cardPointsTowardRoundTotal,
  legalPlays,
  trickWinner,
} = require("./tricks");
const {
  detectCombinations,
  compareTeams,
  compareDeclarations,
  strongestDeclaration,
  canDeclareBellaOnPlay,
  BELLA_POINTS,
  declarationComboKey,
  sumComboPoints,
} = require("./declarations");
const { scoreRound } = require("./scoring");

/**
 * @typedef {import('./snapshot').BelotArmenianCommandType} BelotArmenianCommandType
 * @typedef {import('./snapshot').BelotArmenianEventType} BelotArmenianEventType
 */

/**
 * @typedef {{ type: string, seat?: number, bid?: Record<string, unknown>, payload?: Record<string, unknown> }} BelotArmenianCommand
 */

/**
 * @typedef {{ type: BelotArmenianEventType, payload?: Record<string, unknown> }} BelotArmenianEvent
 */

/** Внутренние пороги матча (полные очки); на экране делятся на 10 → 151/301/501/1001 CP. */
/** @type {readonly [1510, 3010, 5010, 10010]} */
const ALLOWED_TARGET_SCORES = Object.freeze([1510, 3010, 5010, 10010]);
const DEFAULT_TARGET_SCORE = 10010;

/**
 * @param {unknown} value
 * @returns {value is 1510|3010|5010|10010}
 */
function isAllowedTargetScore(value) {
  return value === 1510 || value === 3010 || value === 5010 || value === 10010;
}

/**
 * @param {unknown} config
 * @returns {{ targetScore: 1510|3010|5010|10010, rng: () => number }}
 */
/**
 * @param {unknown} v
 * @returns {'casual'|'hardcore'}
 */
function normalizeDeclarationMode(v) {
  return v === "hardcore" ? "hardcore" : "casual";
}

function normalizeEngineConfig(config) {
  const raw = config && typeof config === "object" ? config : {};
  const ts = raw.targetScore;
  const targetScore =
    ts === undefined ? DEFAULT_TARGET_SCORE : Number(ts);
  if (!isAllowedTargetScore(targetScore)) {
    throw new Error(
      `armenian/engine: targetScore must be 1510, 3010, 5010 or 10010, got ${String(ts)}`,
    );
  }
  /** @type {() => number} */
  let rng;
  /** @type {{ seed: number, mulberryA: number, draws: number } | null} */
  let rngMeta = null;
  if (typeof raw.rng === "function") {
    rng = raw.rng;
  } else {
    const seed =
      raw.rngSeed != null
        ? Number(raw.rngSeed) >>> 0
        : (Math.random() * 0x100000000) >>> 0;
    rngMeta = createRngMeta(seed);
    rng = rngFromMeta(rngMeta);
  }
  const declarationMode = normalizeDeclarationMode(raw.declarationMode);
  const shulerEnabled = Boolean(raw.shulerEnabled);
  return { targetScore, rng, declarationMode, shulerEnabled, rngMeta };
}

/** В полных очках матча; на HUD в CP — деление teamScores на 10 (как взятки). ×10 к лимиту матча. */
const SHULER_CATCH_BONUS_TENS = 160;
const SHULER_FALSE_ACCUSE_PENALTY_TENS = 100;

/**
 * @param {number[][]} hands
 * @returns {number[][]}
 */
function cloneHands(hands) {
  return hands.map((h) => h.slice());
}

/**
 * @param {import('./declarations').BelotDeclaration[]} combos
 * @returns {import('./declarations').BelotDeclaration[]}
 */
function sequenceOnly(combos) {
  return combos.filter((c) => c && c.kind === "SEQUENCE");
}

/**
 * @param {import('./declarations').BelotDeclaration|null} seq
 * @returns {{ label: string, length: number, highRank: number, points: number }|null}
 */
function toSequenceBrief(seq) {
  if (!seq || seq.kind !== "SEQUENCE") return null;
  return {
    label: String(seq.label || ""),
    length: Number(seq.length || 0),
    highRank: Number(seq.highRank || -1),
    points: Number(seq.points || 0),
  };
}

/**
 * @param {import('./declarations').BelotDeclaration|null} a
 * @param {import('./declarations').BelotDeclaration|null} b
 * @returns {{ winner: 'team_a'|'team_b'|'tie', reason: 'none'|'length'|'highRank' }}
 */
function compareTopSequences(a, b, contractTrumpSuit) {
  if (!a && !b) return { winner: "tie", reason: "none" };
  if (a && !b) return { winner: "team_a", reason: "none" };
  if (!a && b) return { winner: "team_b", reason: "none" };
  const lenA = Number(a?.length || 0);
  const lenB = Number(b?.length || 0);
  if (lenA !== lenB) return { winner: lenA > lenB ? "team_a" : "team_b", reason: "length" };
  const hiA = Number(a?.highRank || -1);
  const hiB = Number(b?.highRank || -1);
  if (hiA !== hiB) return { winner: hiA > hiB ? "team_a" : "team_b", reason: "highRank" };
  const cmp = compareDeclarations(
    /** @type {import('./declarations').BelotDeclaration} */ (a),
    /** @type {import('./declarations').BelotDeclaration} */ (b),
    contractTrumpSuit,
  );
  if (cmp !== 0) {
    return { winner: cmp > 0 ? "team_a" : "team_b", reason: "highRank" };
  }
  return { winner: "tie", reason: "none" };
}

/**
 * @param {any} state
 * @param {number} seat
 * @param {string[]} declaredKeys
 * @returns {any}
 */
function stateAfterCommitFirstPlayDeclarations(state, seat, declaredKeys) {
  const h0 = state.handsAtRoundStart?.[seat];
  if (!h0 || h0.length !== 8) {
    throw new Error("armenian/engine: commit declarations — no snapshot hand for seat");
  }
  const all = detectCombinations(h0);
  const valid = new Set(all.map(declarationComboKey));
  if (!Array.isArray(declaredKeys)) {
    throw new Error("armenian/engine: declaredKeys must be an array");
  }
  const seen = new Set();
  for (const k of declaredKeys) {
    if (typeof k !== "string" || !valid.has(k)) {
      throw new Error(`armenian/engine: invalid declared combo key: ${String(k)}`);
    }
    if (seen.has(k)) throw new Error("armenian/engine: duplicate declared combo key");
    seen.add(k);
  }
  if (state.firstPlayDeclarationsCommitted[seat]) {
    throw new Error("armenian/engine: declarations already committed for this seat");
  }
  const declaredComboKeysBySeat = state.declaredComboKeysBySeat.map((row, i) =>
    i === seat ? declaredKeys.slice() : row.slice(),
  );
  const firstPlayDeclarationsCommitted = state.firstPlayDeclarationsCommitted.map((c, i) =>
    i === seat ? true : c,
  );
  return { ...state, declaredComboKeysBySeat, firstPlayDeclarationsCommitted };
}

/**
 * @param {BelotArmenianCommand} cmd
 * @returns {void}
 */
function assertSeat(cmd) {
  if (typeof cmd.seat !== "number" || !Number.isInteger(cmd.seat) || cmd.seat < 0 || cmd.seat > 3) {
    throw new Error("armenian/engine: command requires seat 0..3");
  }
}

/**
 * @param {Partial<{ targetScore: number, rng: () => number }>} [config]
 */
function createInitialState(config) {
  const { targetScore, rng, declarationMode, shulerEnabled, rngMeta } = normalizeEngineConfig(
    config || {},
  );
  return {
    phase: "init",
    targetScore,
    declarationMode,
    /** Режим «Шулерство»: разрешены намеренно нелегальные ходы (онлайн, флаг комнаты). */
    shulerEnabled,
    /** Mulberry32 meta when не передан кастомный `rng`; иначе `null`. */
    rngMeta,
    /** Накопленные за текущий раунд очки от поимок/ложных обвинений; входят в delta при ROUND_SCORED. */
    shulerBonusByTeam: /** @type {[number, number]} */ ([0, 0]),
    cheaterRevealedSeat: /** @type {number|null} */ (null),
    /** @type {{ type: 'none'|'caught'|'false_accuse', accuserSeat?: number, cheaterSeat?: number, card?: number }} */
    lastAccusationOutcome: { type: "none" },
    teamScores: /** @type {[number, number]} */ ([0, 0]),
    rng,
    dealerSeat: 0,
    hands: null,
    handsAtRoundStart: null,
    auction: null,
    contract: null,
    trickIndex: 0,
    trickLeadSeat: null,
    currentTrick: /** @type {{ seat: number, card: number }[]} */ ([]),
    currentTurnSeat: null,
    tricksWonByTeam: /** @type {[number, number]} */ ([0, 0]),
    trickCardsByTeam: /** @type {[number[], number[]]} */ ([[], []]),
    lastTrickWinnerSeat: null,
    /** Последняя завершённая взятка (для UI «посмотреть взятку»); обновляется при каждом TRICK_WON. */
    lastCompletedTrick: null,
    declarationPointsByTeam: /** @type {[number, number]} */ ([0, 0]),
    declarationGrossByTeam: /** @type {[number, number]} */ ([0, 0]),
    bellaBonusByTeam: /** @type {[number, number]} */ ([0, 0]),
    /** В хардкоре: после первого выбора «объявить/скрыть» для места — true. */
    firstPlayDeclarationsCommitted: /** @type {[boolean, boolean, boolean, boolean]} */ ([
      false,
      false,
      false,
      false,
    ]),
    /** В хардкоре: ключи {@link declarationComboKey}, которые игрок объявил (остальные — скрыты). */
    declaredComboKeysBySeat: /** @type {[string[], string[], string[], string[]]} */ ([
      [],
      [],
      [],
      [],
    ]),
    roundIndex: 0,
  };
}

/**
 * @param {any} s
 * @returns {boolean}
 */
function hasMatchWinner(s) {
  return s.teamScores[0] >= s.targetScore || s.teamScores[1] >= s.targetScore;
}

/**
 * @param {any} state
 * @param {BelotArmenianEvent[]} events
 * @returns {any}
 */
function finishRoundScoring(state, events) {
  const c = state.contract;
  if (!c) throw new Error("armenian/engine: internal — no contract at score");
  const trump = c.trumpSuit;
  const tcp = /** @type {[number, number]} */ ([
    state.trickCardsByTeam[0].reduce((a, card) => a + cardPointsTowardRoundTotal(card, trump), 0),
    state.trickCardsByTeam[1].reduce((a, card) => a + cardPointsTowardRoundTotal(card, trump), 0),
  ]);
  const lastTeam =
    state.lastTrickWinnerSeat == null
      ? 0
      : teamOfSeat(state.lastTrickWinnerSeat);

  /** @type {[number, number]} */
  let declComboPts = [0, 0];
  /** @type {number|undefined} */
  let baitLoserDecl;
  if (state.declarationMode === "hardcore" && state.handsAtRoundStart) {
    const hSnap = state.handsAtRoundStart;
    /** @param {number[]} seats */
    function teamDeclaredCombos(seats) {
      /** @type {import('./declarations').BelotDeclaration[]} */
      const out = [];
      for (const s of seats) {
        const all = detectCombinations(hSnap[s]);
        const declaredSet = new Set(state.declaredComboKeysBySeat[s] || []);
        for (const combo of all) {
          if (declaredSet.has(declarationComboKey(combo))) out.push(combo);
        }
      }
      return out;
    }
    const aCombos = teamDeclaredCombos([0, 2]);
    const bCombos = teamDeclaredCombos([1, 3]);
    const res = compareTeams(aCombos, bCombos, c.trumpSuit);
    declComboPts = [sumComboPoints(res.scoringA), sumComboPoints(res.scoringB)];
    const rawA = sumComboPoints(aCombos);
    const rawB = sumComboPoints(bCombos);
    baitLoserDecl =
      (c.contractTeam === 0 ? rawA : rawB) + state.bellaBonusByTeam[c.contractTeam];
  } else {
    declComboPts = [
      state.declarationPointsByTeam[0],
      state.declarationPointsByTeam[1],
    ];
    baitLoserDecl =
      state.declarationGrossByTeam[c.contractTeam] +
      state.bellaBonusByTeam[c.contractTeam];
  }

  const decl = /** @type {[number, number]} */ ([
    declComboPts[0] + state.bellaBonusByTeam[0],
    declComboPts[1] + state.bellaBonusByTeam[1],
  ]);
  const sr = scoreRound({
    contractTeam: c.contractTeam,
    contractTargetPoints: c.contractTargetPoints,
    contraMultiplier: c.contraMultiplier,
    kapotBid: c.kapot,
    noTrumpContract: c.trumpSuit == null,
    tricksWonByTeam: state.tricksWonByTeam,
    trickCardPointsByTeam: tcp,
    lastTrickWinnerTeam: lastTeam,
    declarationPointsByTeam: decl,
    losingContractTeamDeclarationPointsForBait: baitLoserDecl,
    shulerBonusByTeam: state.shulerBonusByTeam,
  });
  const teamScores = /** @type {[number, number]} */ ([
    state.teamScores[0] + sr.delta[0],
    state.teamScores[1] + sr.delta[1],
  ]);
  events.push({
    type: "ROUND_SCORED",
    payload: {
      delta: sr.delta,
      success: sr.success,
      bait: sr.bait,
      contractTeamTrickPlusDecl: sr.contractTeamTrickPlusDecl,
    },
  });
  let phase = "dealing";
  let more = /** @type {BelotArmenianEvent[]} */ ([]);
  if (teamScores[0] >= state.targetScore || teamScores[1] >= state.targetScore) {
    phase = "match_end";
    more.push({ type: "MATCH_ENDED", payload: { teamScores } });
  }
  const dealerSeat = (state.dealerSeat + 1) % 4;
  for (const m of more) events.push(m);
  return {
    ...state,
    phase,
    teamScores,
    dealerSeat,
    lastCompletedTrick: null,
    hands: null,
    handsAtRoundStart: null,
    auction: null,
    contract: null,
    trickIndex: 0,
    trickLeadSeat: null,
    currentTrick: [],
    currentTurnSeat: null,
    tricksWonByTeam: [0, 0],
    trickCardsByTeam: [[], []],
    lastTrickWinnerSeat: null,
    declarationPointsByTeam: [0, 0],
    declarationGrossByTeam: [0, 0],
    bellaBonusByTeam: [0, 0],
    shulerBonusByTeam: [0, 0],
    cheaterRevealedSeat: null,
    lastAccusationOutcome: { type: "none" },
    firstPlayDeclarationsCommitted: [false, false, false, false],
    declaredComboKeysBySeat: [[], [], [], []],
    roundIndex: state.roundIndex + 1,
  };
}

/**
 * @param {any} state
 * @param {BelotArmenianCommand} cmd
 * @returns {{ state: any, events: BelotArmenianEvent[] }}
 */
function applyCommand(state, cmd) {
  if (!state || typeof state !== "object") {
    throw new TypeError("armenian/engine: state required");
  }
  if (!cmd || typeof cmd.type !== "string") {
    throw new Error("armenian/engine: command required");
  }

  /** @type {BelotArmenianEvent[]} */
  const events = [];

  if (state.phase === "match_end" && cmd.type !== "START_ROUND") {
    throw new Error("armenian/engine: match ended — only START_ROUND to reset");
  }

  if (cmd.type === "START_ROUND") {
    let base = state;
    if (state.phase === "match_end") {
      base = {
        ...state,
        phase: "init",
        teamScores: [0, 0],
        roundIndex: 0,
        shulerBonusByTeam: [0, 0],
        cheaterRevealedSeat: null,
        lastAccusationOutcome: { type: "none" },
      };
    }
    if (base.phase !== "init" && base.phase !== "dealing") {
      throw new Error("armenian/engine: START_ROUND only from init, dealing, or match_end");
    }
    if (hasMatchWinner(base) && base.phase === "dealing") {
      throw new Error("armenian/engine: match already won");
    }
    const deck = buildDeck();
    shuffle(deck, base.rng);
    const { hands } = deal323(deck);
    const firstSpeaker = (base.dealerSeat + 1) % 4;
    return {
      state: {
        ...base,
        phase: "auction",
        hands,
        handsAtRoundStart: cloneHands(hands),
        auction: createInitialAuctionState(),
        contract: null,
        tricksWonByTeam: [0, 0],
        trickCardsByTeam: [[], []],
        trickIndex: 0,
        currentTrick: [],
        trickLeadSeat: null,
        currentTurnSeat: firstSpeaker,
        lastTrickWinnerSeat: null,
        lastCompletedTrick: null,
        declarationPointsByTeam: [0, 0],
        declarationGrossByTeam: [0, 0],
        bellaBonusByTeam: [0, 0],
        declarationMode: normalizeDeclarationMode(base.declarationMode),
        firstPlayDeclarationsCommitted: [false, false, false, false],
        declaredComboKeysBySeat: [[], [], [], []],
        shulerBonusByTeam: [0, 0],
        cheaterRevealedSeat: null,
        lastAccusationOutcome: { type: "none" },
      },
      events,
    };
  }

  if (
    state.phase === "auction" &&
    (cmd.type === "BID" ||
      cmd.type === "PASS" ||
      cmd.type === "CONTRA" ||
      cmd.type === "RECONTRA")
  ) {
    assertSeat(cmd);
    if (state.currentTurnSeat !== cmd.seat) {
      throw new Error("armenian/engine: auction — not this seat's turn to speak");
    }
    if (!state.auction) throw new Error("armenian/engine: no auction");
    const auc = state.auction;

    if (cmd.type === "CONTRA" || cmd.type === "RECONTRA") {
      if (auc.phase !== "bidding" || auc.highestBid == null || auc.bidAuthorSeat == null) {
        throw new Error("armenian/engine: contra/recontra only during open bidding with a bid");
      }
      const contractTeam = teamOfSeat(auc.bidAuthorSeat);
      const speakerTeam = teamOfSeat(cmd.seat);
      if (cmd.type === "CONTRA") {
        if (speakerTeam === contractTeam) {
          throw new Error("armenian/engine: contra only for defenders (opponents of contract)");
        }
      } else if (speakerTeam !== contractTeam) {
        throw new Error("armenian/engine: recontra only for the contract team");
      }
      const ar = applyAuctionCommand(auc, { type: cmd.type, seat: cmd.seat });
      if (!ar.ok) throw new Error(ar.error);
      for (const ev of ar.events) {
        if (ev.type === "CONTRA_APPLIED" || ev.type === "RECONTRA_APPLIED") {
          events.push({ type: ev.type, payload: { multiplier: ev.multiplier } });
        } else {
          events.push({ type: ev.type, payload: ev });
        }
      }
      return {
        state: {
          ...state,
          auction: ar.state,
          currentTurnSeat: (cmd.seat + 1) % 4,
        },
        events,
      };
    }

    const bid =
      cmd.type === "BID"
        ? {
            type: "BID",
            seat: cmd.seat,
            bid: cmd.bid || /** @type {any} */ (cmd.payload)?.bid,
          }
        : { type: "PASS", seat: cmd.seat };
    if (cmd.type === "BID" && !bid.bid) throw new Error("armenian/engine: BID requires bid");
    const ar = applyAuctionCommand(state.auction, bid);
    if (!ar.ok) throw new Error(ar.error);
    let next = { ...state, auction: ar.state };
    let toDeclare = false;
    let redeal = false;
    for (const ev of ar.events) {
      if (ev.type === "AUCTION_REDEAL") {
        redeal = true;
        events.push({ type: "AUCTION_REDEAL", payload: {} });
        const deck = buildDeck();
        shuffle(deck, state.rng);
        const { hands } = deal323(deck);
        next = {
          ...next,
          hands,
          handsAtRoundStart: cloneHands(hands),
          auction: createInitialAuctionState(),
        };
      }
      if (ev.type === "AUCTION_ENDED") {
        const b = ev.bid;
        const trumpSuit =
          b.trump === TRUMP_NO_TRUMP ? null : b.trump === TRUMP_ALL_TRUMP ? TRUMP_ALL_TRUMP : b.trump;
        const contractTargetPoints = b.kapot ? KAPOT_CONTRACT_POINTS : b.tens * 10;
        const contractTeam = teamOfSeat(ev.bidAuthorSeat);
        toDeclare = true;
        next = {
          ...next,
          phase: "declare",
          auction: ar.state,
          contract: {
            bidAuthorSeat: ev.bidAuthorSeat,
            tens: b.tens,
            trump: b.trump,
            kapot: Boolean(b.kapot),
            trumpSuit,
            contractTeam,
            contractTargetPoints,
            contraMultiplier: ar.state.contraMultiplier,
          },
          currentTurnSeat: null,
        };
        events.push({
          type: "AUCTION_ENDED",
          payload: { bid: b, bidAuthorSeat: ev.bidAuthorSeat },
        });
      }
    }
    if (!toDeclare) {
      if (redeal) {
        next = { ...next, currentTurnSeat: (next.dealerSeat + 1) % 4 };
      } else {
        next = { ...next, currentTurnSeat: (cmd.seat + 1) % 4 };
      }
    }
    return { state: next, events };
  }

  if (state.phase === "declare" && (cmd.type === "CONTRA" || cmd.type === "RECONTRA")) {
    throw new Error(
      "armenian/engine: contra and recontra are played during the auction (bidding), not in declare",
    );
  }

  if (cmd.type === "END_DECLARE_PHASE") {
    if (state.phase !== "declare") {
      throw new Error("armenian/engine: END_DECLARE_PHASE only in declare");
    }
    if (!state.handsAtRoundStart || !state.contract) {
      throw new Error("armenian/engine: missing hands or contract");
    }
    const h = state.handsAtRoundStart;
    const lead = (state.dealerSeat + 1) % 4;
    const mode = normalizeDeclarationMode(state.declarationMode);

    if (mode === "casual") {
      const ctr = state.contract.trumpSuit;
      const aCombos = [...detectCombinations(h[0]), ...detectCombinations(h[2])];
      const bCombos = [...detectCombinations(h[1]), ...detectCombinations(h[3])];
      const grossA = sumComboPoints(aCombos);
      const grossB = sumComboPoints(bCombos);
      const res = compareTeams(aCombos, bCombos, ctr);
      const scoringA = sumComboPoints(res.scoringA);
      const scoringB = sumComboPoints(res.scoringB);
      const topSeqA = strongestDeclaration(sequenceOnly(aCombos), ctr);
      const topSeqB = strongestDeclaration(sequenceOnly(bCombos), ctr);
      const topSequenceCompare = compareTopSequences(topSeqA, topSeqB, ctr);
      const sequenceBurnedA = sumComboPoints(sequenceOnly(res.burnedA));
      const sequenceBurnedB = sumComboPoints(sequenceOnly(res.burnedB));
      events.push({
        type: "DECLARATIONS_RESOLVED",
        payload: {
          outcome: res.outcome,
          scoringA,
          scoringB,
          mode: "casual",
          topSequenceA: toSequenceBrief(topSeqA),
          topSequenceB: toSequenceBrief(topSeqB),
          sequenceWinner: topSequenceCompare.winner,
          sequenceTieBreak: topSequenceCompare.reason,
          sequenceBurnedA,
          sequenceBurnedB,
        },
      });
      events.push({
        type: "TRICK_STARTED",
        payload: { trickIndex: 1, trickLeadSeat: lead },
      });
      return {
        state: {
          ...state,
          phase: "play",
          declarationPointsByTeam: [scoringA, scoringB],
          declarationGrossByTeam: [grossA, grossB],
          trickIndex: 1,
          trickLeadSeat: lead,
          currentTrick: [],
          currentTurnSeat: lead,
          lastCompletedTrick: null,
          firstPlayDeclarationsCommitted: [true, true, true, true],
          declaredComboKeysBySeat: [[], [], [], []],
        },
        events,
      };
    }

    /** @type {[boolean, boolean, boolean, boolean]} */
    const firstCommit = [false, false, false, false];
    for (let s = 0; s < 4; s += 1) {
      if (detectCombinations(h[s]).length === 0) firstCommit[s] = true;
    }
    events.push({
      type: "DECLARATIONS_RESOLVED",
      payload: {
        outcome: "hardcore_pending",
        scoringA: 0,
        scoringB: 0,
        mode: "hardcore",
        topSequenceA: null,
        topSequenceB: null,
        sequenceWinner: "tie",
        sequenceTieBreak: "none",
        sequenceBurnedA: 0,
        sequenceBurnedB: 0,
      },
    });
    events.push({
      type: "TRICK_STARTED",
      payload: { trickIndex: 1, trickLeadSeat: lead },
    });
    return {
      state: {
        ...state,
        phase: "play",
        declarationPointsByTeam: [0, 0],
        declarationGrossByTeam: [0, 0],
        trickIndex: 1,
        trickLeadSeat: lead,
        currentTrick: [],
        currentTurnSeat: lead,
        lastCompletedTrick: null,
        firstPlayDeclarationsCommitted: firstCommit,
        declaredComboKeysBySeat: [[], [], [], []],
      },
      events,
    };
  }

  if (cmd.type === "COMMIT_FIRST_PLAY_DECLARATIONS") {
    if (state.phase !== "play") {
      throw new Error("armenian/engine: COMMIT_FIRST_PLAY_DECLARATIONS only in play");
    }
    if (normalizeDeclarationMode(state.declarationMode) !== "hardcore") {
      throw new Error("armenian/engine: COMMIT_FIRST_PLAY_DECLARATIONS only in hardcore mode");
    }
    assertSeat(cmd);
    const keys = cmd.payload?.declaredKeys;
    if (!Array.isArray(keys)) {
      throw new Error("armenian/engine: COMMIT_FIRST_PLAY_DECLARATIONS requires payload.declaredKeys array");
    }
    const nextS = stateAfterCommitFirstPlayDeclarations(state, cmd.seat, /** @type {string[]} */ (keys));
    events.push({
      type: "FIRST_PLAY_DECL_COMMIT",
      payload: {
        seat: cmd.seat,
        declaredKeys: keys,
        declaredCount: keys.length,
        via: "COMMIT_FIRST_PLAY_DECLARATIONS",
      },
    });
    return { state: nextS, events };
  }

  if (cmd.type === "ACCUSE_DIRTY") {
    if (state.phase !== "play") throw new Error("armenian/engine: ACCUSE_DIRTY only in play");
    if (!state.shulerEnabled) throw new Error("armenian/engine: ACCUSE_DIRTY — shuler mode off");
    assertSeat(cmd);
    const cardRaw = cmd.payload && Object.prototype.hasOwnProperty.call(cmd.payload, "card") ? cmd.payload.card : NaN;
    const card = Number(cardRaw);
    if (!Number.isInteger(card)) throw new Error("armenian/engine: ACCUSE_DIRTY needs integer payload.card");
    const trick = state.currentTrick;
    if (!Array.isArray(trick) || trick.length === 0) {
      throw new Error("armenian/engine: ACCUSE_DIRTY — no cards on table");
    }
    const entryIdx = trick.findIndex((p) => p && p.card === card);
    if (entryIdx < 0) throw new Error("armenian/engine: ACCUSE_DIRTY — card not in current trick");
    /** @type {any} */
    const entry = trick[entryIdx];
    const cardSeat = entry.seat;
    if (teamOfSeat(cmd.seat) === teamOfSeat(cardSeat)) {
      throw new Error("armenian/engine: ACCUSE_DIRTY — cannot accuse your own team");
    }
    const accTeam = teamOfSeat(cmd.seat);
    const falseKey = accTeam === 0 ? "falseAccuseTeam0" : "falseAccuseTeam1";

    if (entry.dirty === true && !entry.caughtResolved) {
      const cheaterSeat = entry.cheaterSeat != null ? entry.cheaterSeat : cardSeat;
      const shulerBonusByTeam = /** @type {[number, number]} */ ([
        state.shulerBonusByTeam[0] + (accTeam === 0 ? SHULER_CATCH_BONUS_TENS : 0),
        state.shulerBonusByTeam[1] + (accTeam === 1 ? SHULER_CATCH_BONUS_TENS : 0),
      ]);
      const newTrick = trick.map((p, i) =>
        i === entryIdx ? { ...p, caughtResolved: true } : p,
      );
      const lastAccusationOutcome = {
        type: /** @type {'caught'} */ ("caught"),
        accuserSeat: cmd.seat,
        cheaterSeat,
        card,
      };
      events.push({
        type: "DIRTY_CAUGHT",
        payload: { accuserSeat: cmd.seat, cheaterSeat, card },
      });
      return {
        state: {
          ...state,
          currentTrick: newTrick,
          shulerBonusByTeam,
          cheaterRevealedSeat: cheaterSeat,
          lastAccusationOutcome,
        },
        events,
      };
    }

    if (entry.dirty === true && entry.caughtResolved) {
      return { state, events: [] };
    }

    if (entry[falseKey] === true) {
      return { state, events: [] };
    }

    const shulerBonusByTeam = /** @type {[number, number]} */ ([
      state.shulerBonusByTeam[0] - (accTeam === 0 ? SHULER_FALSE_ACCUSE_PENALTY_TENS : 0),
      state.shulerBonusByTeam[1] - (accTeam === 1 ? SHULER_FALSE_ACCUSE_PENALTY_TENS : 0),
    ]);
    const newTrick = trick.map((p, i) =>
      i === entryIdx ? { ...p, [falseKey]: true } : p,
    );
    const lastAccusationOutcome = {
      type: /** @type {'false_accuse'} */ ("false_accuse"),
      accuserSeat: cmd.seat,
      card,
    };
    events.push({
      type: "DIRTY_FALSE_ACCUSE",
      payload: { accuserSeat: cmd.seat, card },
    });
    return {
      state: {
        ...state,
        currentTrick: newTrick,
        shulerBonusByTeam,
        cheaterRevealedSeat: null,
        lastAccusationOutcome,
      },
      events,
    };
  }

  if (cmd.type === "PLAY_CARD") {
    if (state.phase !== "play") throw new Error("armenian/engine: PLAY_CARD only in play");
    assertSeat(cmd);
    const pl = cmd.payload;
    const card =
      pl && Object.prototype.hasOwnProperty.call(pl, "card")
        ? Number(pl.card)
        : NaN;
    if (!Number.isInteger(card)) {
      throw new Error("armenian/engine: PLAY_CARD needs integer payload.card");
    }
    if (cmd.seat !== state.currentTurnSeat) {
      throw new Error("armenian/engine: not this seat's turn");
    }
    /** @type {any} */
    let working = state;
    if (normalizeDeclarationMode(state.declarationMode) === "hardcore" && state.handsAtRoundStart) {
      const hSnap = state.handsAtRoundStart[cmd.seat];
      if (hSnap && hSnap.length === 8) {
        const combos = detectCombinations(hSnap);
        if (combos.length > 0 && !state.firstPlayDeclarationsCommitted[cmd.seat]) {
          const pk = pl?.commitDeclaredKeys;
          if (!Array.isArray(pk)) {
            throw new Error(
              "armenian/engine: hardcore — before the first card, pass payload.commitDeclaredKeys (subset of your combos, or [] to hide all) or send COMMIT_FIRST_PLAY_DECLARATIONS",
            );
          }
          working = stateAfterCommitFirstPlayDeclarations(state, cmd.seat, pk);
          events.push({
            type: "FIRST_PLAY_DECL_COMMIT",
            payload: {
              seat: cmd.seat,
              declaredKeys: pk,
              declaredCount: pk.length,
              via: "PLAY_CARD",
            },
          });
        }
      }
    }
    const c = working.contract;
    if (!c || !working.hands) throw new Error("armenian/engine: invalid play state");
    const hand = working.hands[cmd.seat];
    const idx = hand.indexOf(card);
    if (idx < 0) throw new Error("armenian/engine: card not in hand");
    const leadSeat = working.trickLeadSeat;
    if (leadSeat == null) throw new Error("armenian/engine: no lead seat");
    const legal = legalPlays(hand, working.currentTrick, c.trumpSuit, leadSeat);
    let dirtyPlay = false;
    if (!legal.includes(card)) {
      if (!working.shulerEnabled) throw new Error("armenian/engine: illegal card");
      dirtyPlay = true;
      if (Boolean(cmd.payload?.declareBella)) {
        throw new Error("armenian/engine: Bella not allowed on illegal play");
      }
    }
    const declareBella = Boolean(cmd.payload?.declareBella);
    if (declareBella) {
      const ok = c.trumpSuit === TRUMP_ALL_TRUMP ? false : canDeclareBellaOnPlay(card, hand, c.trumpSuit);
      if (!ok) throw new Error("armenian/engine: Bella not allowed");
    }
    const newHand = hand.filter((_, i) => i !== idx);
    const hands = working.hands.map((row, si) => (si === cmd.seat ? newHand : row));
    /** @type {any} */
    const playEntry =
      working.shulerEnabled
        ? {
            seat: cmd.seat,
            card,
            falseAccuseTeam0: false,
            falseAccuseTeam1: false,
            ...(dirtyPlay
              ? {
                  dirty: true,
                  cheaterSeat: cmd.seat,
                  caughtResolved: false,
                }
              : {}),
          }
        : { seat: cmd.seat, card };
    const trick = [...working.currentTrick, playEntry];
    events.push({
      type: "CARD_PLAYED",
      payload: { seat: cmd.seat, card, trickIndex: working.trickIndex },
    });
    if (dirtyPlay) {
      events.push({
        type: "CARD_PLAYED_DIRTY",
        payload: { seat: cmd.seat, card, trickIndex: working.trickIndex },
      });
    }
    let bellaBonusByTeam = working.bellaBonusByTeam;
    if (declareBella) {
      const tm = teamOfSeat(cmd.seat);
      bellaBonusByTeam = /** @type {[number, number]} */ ([
        tm === 0 ? working.bellaBonusByTeam[0] + BELLA_POINTS : working.bellaBonusByTeam[0],
        tm === 1 ? working.bellaBonusByTeam[1] + BELLA_POINTS : working.bellaBonusByTeam[1],
      ]);
    }
    if (trick.length < 4) {
      const nextSeat = (cmd.seat + 1) % 4;
      return {
        state: {
          ...working,
          hands,
          currentTrick: trick,
          currentTurnSeat: nextSeat,
          bellaBonusByTeam,
        },
        events,
      };
    }
    const winnerSeat = trickWinner(trick, c.trumpSuit);
    const lastCompletedTrick = {
      trickIndex: working.trickIndex,
      winnerSeat,
      plays: trick.map((p) => ({ seat: p.seat, card: p.card })),
    };
    const wTeam = teamOfSeat(winnerSeat);
    const tricksWonByTeam = /** @type {[number, number]} */ ([
      working.tricksWonByTeam[0] + (wTeam === 0 ? 1 : 0),
      working.tricksWonByTeam[1] + (wTeam === 1 ? 1 : 0),
    ]);
    const trickCardsByTeam = /** @type {[number[], number[]]} */ ([
      working.trickCardsByTeam[0].concat(
        trick.filter((p) => teamOfSeat(p.seat) === 0).map((p) => p.card),
      ),
      working.trickCardsByTeam[1].concat(
        trick.filter((p) => teamOfSeat(p.seat) === 1).map((p) => p.card),
      ),
    ]);
    events.push({
      type: "TRICK_WON",
      payload: {
        winnerSeat,
        trickIndex: working.trickIndex,
        trick,
      },
    });
    const isLast = working.trickIndex === 8;
    if (!isLast) {
      const nextIdx = working.trickIndex + 1;
      events.push({
        type: "TRICK_STARTED",
        payload: { trickIndex: nextIdx, trickLeadSeat: winnerSeat },
      });
      return {
        state: {
          ...working,
          hands,
          currentTrick: [],
          trickIndex: nextIdx,
          trickLeadSeat: winnerSeat,
          currentTurnSeat: winnerSeat,
          tricksWonByTeam,
          trickCardsByTeam,
          lastTrickWinnerSeat: winnerSeat,
          lastCompletedTrick,
          bellaBonusByTeam,
          cheaterRevealedSeat: null,
          lastAccusationOutcome: { type: "none" },
        },
        events,
      };
    }
    let nextState = {
      ...working,
      hands,
      currentTrick: [],
      trickIndex: 10,
      trickLeadSeat: winnerSeat,
      currentTurnSeat: null,
      tricksWonByTeam,
      trickCardsByTeam,
      lastTrickWinnerSeat: winnerSeat,
      lastCompletedTrick,
      bellaBonusByTeam,
      cheaterRevealedSeat: null,
      lastAccusationOutcome: { type: "none" },
    };
    const scored = finishRoundScoring(nextState, events);
    return { state: scored, events };
  }

  if (cmd.type === "DECLARE_BELLA") {
    throw new Error("armenian/engine: use PLAY_CARD with payload.declareBella");
  }

  throw new Error(`armenian/engine: unsupported command ${cmd.type} in phase ${state.phase}`);
}

module.exports = {
  ALLOWED_TARGET_SCORES,
  DEFAULT_TARGET_SCORE,
  SHULER_CATCH_BONUS_TENS,
  SHULER_FALSE_ACCUSE_PENALTY_TENS,
  createInitialState,
  applyCommand,
};
